export type ReportType = 'spam' | 'insultos' | 'contenido-ofensivo' | 'publicidad-no-autorizada' | 'suplantacion';

export type ReportFlowState = 'reportado' | 'en-revision' | 'resuelto' | 'cerrado';

export type ReportResolution = 'contenido-eliminado' | 'usuario-silenciado' | 'usuario-bloqueado' | 'contenido-ocultado' | 'reporte-rechazado' | 'advertencia-emitida';

export type ReportTargetType = 'comentario-cabina' | 'post-publico' | 'mensaje-privado' | 'perfil-usuario' | 'evento' | 'concurso' | 'foto' | 'video' | 'programa' | 'club';

export interface ReportConfig {
  label: string;
  labelPlural: string;
  icon: string;
  color: string;
  bgColor: string;
  description: string;
}

export interface FlowStateConfig {
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  description: string;
}

export interface ResolutionConfig {
  label: string;
  icon: string;
  color: string;
  bgColor: string;
}

export interface ReportEntry {
  id: string;
  reportType: ReportType;
  reportedBy: string;
  reportedByUsername: string;
  reportedByPhotoUrl: string;
  reportedTarget: string;
  reportedTargetUsername: string;
  targetType: ReportTargetType;
  targetTypeLabel: string;
  reason: string;
  evidenceSummary: string;
  flowState: ReportFlowState;
  assignedReviewer?: string;
  assignedReviewerPhotoUrl?: string;
  resolution?: ReportResolution;
  resolutionDetail?: string;
  isRepeatOffender: boolean;
  repeatCount: number;
  createdAt: string;
  createdAtRelative: string;
  updatedAt: string;
  closedAt?: string;
  closedAtRelative?: string;
  context: string;
}

export interface ReincidenciaEntry {
  id: string;
  reportedTargetUsername: string;
  reportedTargetPhotoUrl: string;
  totalReports: number;
  reportTypes: ReportType[];
  lastReportDate: string;
  lastReportDateRelative: string;
  status: 'vigilancia' | 'reincidente' | 'sancionado';
  statusLabel: string;
  previousSanctions: string[];
}

export interface SaDashboardStats {
  totalReports: number;
  reportsPending: number;
  reportsInReview: number;
  reportsResolved: number;
  reportsClosed: number;
  reportsToday: number;
  reportsThisWeek: number;
  reportsThisMonth: number;
  avgResolutionTime: string;
  repeatOffenderRate: string;
  totalReincidencias: number;
  reincidentesActivos: number;
  topReportType: ReportType;
  topReportTypeCount: number;
  resolutionRate: string;
}

export const reportConfig: Record<ReportType, ReportConfig> = {
  spam: {
    label: 'Spam',
    labelPlural: 'Spam',
    icon: 'ri-spam-2-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    description: 'Mensajes repetitivos, enlaces no solicitados, autopromoción excesiva o contenido automatizado',
  },
  insultos: {
    label: 'Insultos',
    labelPlural: 'Insultos',
    icon: 'ri-emotion-unhappy-line',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    description: 'Ataques personales, lenguaje despectivo, humillaciones o agresiones verbales dirigidas',
  },
  'contenido-ofensivo': {
    label: 'Contenido Ofensivo',
    labelPlural: 'Contenido Ofensivo',
    icon: 'ri-error-warning-line',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    description: 'Contenido discriminatorio, violento, sexual no consentido o que promueve el odio',
  },
  'publicidad-no-autorizada': {
    label: 'Publicidad No Autorizada',
    labelPlural: 'Publicidad No Autorizada',
    icon: 'ri-advertisement-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    description: 'Promoción de productos, servicios o marcas sin autorización de la radio o RFW',
  },
  suplantacion: {
    label: 'Suplantación',
    labelPlural: 'Suplantación',
    icon: 'ri-user-unfollow-line',
    color: 'text-slate-700',
    bgColor: 'bg-slate-100',
    description: 'Falsificación de identidad de locutores, moderadores, oyentes o personal de RFW',
  },
};

export const flowStateConfig: Record<ReportFlowState, FlowStateConfig> = {
  reportado: {
    label: 'Reportado',
    icon: 'ri-flag-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    description: 'El reporte fue recibido y está en cola para ser revisado por un moderador',
  },
  'en-revision': {
    label: 'En Revisión',
    icon: 'ri-search-eye-line',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    description: 'Un moderador está analizando el contenido y evaluando si infringe las normas',
  },
  resuelto: {
    label: 'Resuelto',
    icon: 'ri-check-double-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    description: 'Se tomó una acción basada en la revisión: contenido eliminado, usuario sancionado o reporte rechazado',
  },
  cerrado: {
    label: 'Cerrado',
    icon: 'ri-archive-line',
    color: 'text-slate-500',
    bgColor: 'bg-slate-100',
    description: 'El caso está cerrado definitivamente. La acción fue aplicada y verificada',
  },
};

export const resolutionConfig: Record<ReportResolution, ResolutionConfig> = {
  'contenido-eliminado': { label: 'Contenido Eliminado', icon: 'ri-delete-bin-line', color: 'text-red-600', bgColor: 'bg-red-50' },
  'usuario-silenciado': { label: 'Usuario Silenciado', icon: 'ri-volume-mute-line', color: 'text-amber-600', bgColor: 'bg-amber-50' },
  'usuario-bloqueado': { label: 'Usuario Bloqueado', icon: 'ri-forbid-line', color: 'text-red-700', bgColor: 'bg-red-100' },
  'contenido-ocultado': { label: 'Contenido Ocultado', icon: 'ri-eye-off-line', color: 'text-slate-600', bgColor: 'bg-slate-100' },
  'reporte-rechazado': { label: 'Reporte Rechazado', icon: 'ri-close-circle-line', color: 'text-slate-500', bgColor: 'bg-slate-50' },
  'advertencia-emitida': { label: 'Advertencia Emitida', icon: 'ri-alert-line', color: 'text-orange-600', bgColor: 'bg-orange-50' },
};

export const saDashboardStats: SaDashboardStats = {
  totalReports: 189340,
  reportsPending: 287,
  reportsInReview: 142,
  reportsResolved: 187120,
  reportsClosed: 176500,
  reportsToday: 347,
  reportsThisWeek: 2840,
  reportsThisMonth: 12400,
  avgResolutionTime: '8.7 min',
  repeatOffenderRate: '12.4%',
  totalReincidencias: 23420,
  reincidentesActivos: 387,
  topReportType: 'spam',
  topReportTypeCount: 72300,
  resolutionRate: '98.8%',
};

export const reports: ReportEntry[] = [
  // ═══════ SPAM (8 reportes) ═══════
  {
    id: 'rep-001',
    reportType: 'spam',
    reportedBy: 'Andrea Castillo',
    reportedByUsername: '@andreac_oye',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20casual%20friendly%20expression%20clean%20portrait%20warm%20natural%20light%20approachable%20vibe&width=120&height=120&seq=rep-andrea&orientation=squarish',
    reportedTarget: 'Enlaces Rápidos Hoy',
    reportedTargetUsername: '@enlaces_hoy',
    targetType: 'comentario-cabina',
    targetTypeLabel: 'Comentario en Cabina',
    reason: '12 mensajes consecutivos con enlaces a sitios de apuestas en el chat de La Mega durante El Despertador',
    evidenceSummary: '12 mensajes en 4 minutos con URLs acortadas a sitios de apuestas deportivas',
    flowState: 'resuelto',
    assignedReviewer: 'Camila Ortega',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20friendly%20but%20authoritative%20expression%20clean%20modern%20studio%20portrait%20warm%20natural%20lighting&width=80&height=80&seq=rep-camila&orientation=squarish',
    resolution: 'usuario-silenciado',
    resolutionDetail: 'Usuario silenciado por 7 días. Historial de 2 reportes previos por spam.',
    isRepeatOffender: true,
    repeatCount: 3,
    createdAt: '2026-06-21T10:00:00Z',
    createdAtRelative: 'Hace 12 min',
    updatedAt: '2026-06-21T10:08:00Z',
    closedAt: '2026-06-21T10:08:00Z',
    closedAtRelative: 'Hace 4 min',
    context: 'La Mega 107.3 FM · Cabina · El Despertador',
  },
  {
    id: 'rep-002',
    reportType: 'spam',
    reportedBy: 'Miguel Ángel Ruiz',
    reportedByUsername: '@miguelruiz_fan',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20twenties%20casual%20friendly%20smile%20clean%20portrait%20natural%20light%20approachable&width=120&height=120&seq=rep-miguel&orientation=squarish',
    reportedTarget: 'PromoBot 2026',
    reportedTargetUsername: '@promobot_2026',
    targetType: 'post-publico',
    targetTypeLabel: 'Post Público',
    reason: 'Publicación repetitiva promocionando un servicio externo de streaming en 5 clubes distintos',
    evidenceSummary: 'Mismo post publicado en 5 clubes diferentes en intervalo de 8 minutos',
    flowState: 'resuelto',
    assignedReviewer: 'Natalia Romero',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20enthusiastic%20warm%20expression%20community-oriented%20vibe%20modern%20studio%20portrait&width=80&height=80&seq=rep-natalia&orientation=squarish',
    resolution: 'contenido-eliminado',
    resolutionDetail: 'Las 5 publicaciones fueron eliminadas. Usuario advertido.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T09:40:00Z',
    createdAtRelative: 'Hace 32 min',
    updatedAt: '2026-06-21T09:52:00Z',
    closedAt: '2026-06-21T09:52:00Z',
    closedAtRelative: 'Hace 20 min',
    context: 'Clubes RFW · Fanáticos La Kalle, Rockeros Unidos, Vallenato y Más, Cumbia Club, Salsa Brava',
  },
  {
    id: 'rep-003',
    reportType: 'spam',
    reportedBy: 'Valentina Orozco',
    reportedByUsername: '@vale_orozco',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20bright%20smile%20casual%20portrait%20warm%20lighting%20friendly%20expression%20clean%20background&width=120&height=120&seq=rep-vale&orientation=squarish',
    reportedTarget: 'VendeCuentas Pro',
    reportedTargetUsername: '@vendecuentas_pro',
    targetType: 'mensaje-privado',
    targetTypeLabel: 'Mensaje Privado',
    reason: 'Envío masivo de mensajes privados ofreciendo compra de cuentas verificadas a 14 oyentes',
    evidenceSummary: '14 mensajes privados idénticos en 20 minutos con mismo texto de oferta',
    flowState: 'resuelto',
    assignedReviewer: 'Mateo Herrera',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20sharp%20focused%20expression%20neat%20appearance%20professional%20studio%20portrait&width=80&height=80&seq=rep-mateo&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente por spam masivo. Reporte elevado a Super Admin.',
    isRepeatOffender: true,
    repeatCount: 5,
    createdAt: '2026-06-21T08:30:00Z',
    createdAtRelative: 'Hace 1 hora',
    updatedAt: '2026-06-21T08:55:00Z',
    closedAt: '2026-06-21T09:10:00Z',
    closedAtRelative: 'Hace 1 hora',
    context: 'Mensajes Privados · 14 destinatarios distintos',
  },
  {
    id: 'rep-004',
    reportType: 'spam',
    reportedBy: 'Carlos El Fanático',
    reportedByUsername: '@carlos_fanatico',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20passionate%20expression%20casual%20portrait%20natural%20lighting%20energetic%20vibe&width=120&height=120&seq=rep-carlos-fan&orientation=squarish',
    reportedTarget: 'LinkTree 999',
    reportedTargetUsername: '@linktree_999',
    targetType: 'comentario-cabina',
    targetTypeLabel: 'Comentario en Cabina',
    reason: 'Envío de 8 links de afiliados en la cabina de Radio Felicidad durante 10 minutos',
    evidenceSummary: '8 comentarios con enlaces de afiliado a productos de audio y electrónica',
    flowState: 'en-revision',
    assignedReviewer: 'Lucía Vargas',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20caring%20expression%20gentle%20but%20firm%20demeanor%20modern%20studio%20portrait%20warm%20soft%20lighting&width=80&height=80&seq=rep-lucia&orientation=squarish',
    isRepeatOffender: true,
    repeatCount: 2,
    createdAt: '2026-06-21T09:55:00Z',
    createdAtRelative: 'Hace 17 min',
    updatedAt: '2026-06-21T09:55:00Z',
    context: 'Radio Felicidad 88.9 FM · Cabina · El Show de la Mañana',
  },
  {
    id: 'rep-005',
    reportType: 'spam',
    reportedBy: 'Diana Patricia López',
    reportedByUsername: '@dianap_lop',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20late%20twenties%20professional%20casual%20look%20clean%20portrait%20warm%20friendly%20smile%20natural%20lighting&width=120&height=120&seq=rep-diana&orientation=squarish',
    reportedTarget: 'Cadena Reenvío',
    reportedTargetUsername: '@cadena_reenvio',
    targetType: 'post-publico',
    targetTypeLabel: 'Post Público',
    reason: 'Cadena de reenvío pidiendo compartir para ganar premio falso en el feed de la radio',
    evidenceSummary: 'Post con texto de cadena tipo "compartí esto a 10 amigos y ganá un viaje" con enlace externo',
    flowState: 'reportado',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T10:08:00Z',
    createdAtRelative: 'Hace 4 min',
    updatedAt: '2026-06-21T10:08:00Z',
    context: 'Rock & Pop 95.1 FM · Feed Público',
  },
  {
    id: 'rep-006',
    reportType: 'spam',
    reportedBy: 'José Manuel Torres',
    reportedByUsername: '@josemanuel_to',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20friendly%20expression%20casual%20portrait%20warm%20natural%20light%20approachable&width=120&height=120&seq=rep-josemanuel&orientation=squarish',
    reportedTarget: 'Streaming Gratis Ya',
    reportedTargetUsername: '@streaming_gratis',
    targetType: 'evento',
    targetTypeLabel: 'Evento',
    reason: 'Comentarios spam promocionando plataforma de streaming pirata en el foro del RFW Fest',
    evidenceSummary: '5 comentarios con enlaces a sitio de streaming no autorizado en página del evento',
    flowState: 'resuelto',
    assignedReviewer: 'Ricardo El Guardián',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20forties%20experienced%20commanding%20yet%20fair%20expression%20security%20professional%20style%20studio%20portrait&width=80&height=80&seq=rep-ricardo&orientation=squarish',
    resolution: 'contenido-eliminado',
    resolutionDetail: 'Comentarios eliminados. Usuario silenciado por 72 horas.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-20T23:10:00Z',
    createdAtRelative: 'Hace 11 horas',
    updatedAt: '2026-06-20T23:25:00Z',
    closedAt: '2026-06-20T23:25:00Z',
    closedAtRelative: 'Hace 11 horas',
    context: 'RFW Fest 2026 Medellín · Foro de Asistentes',
  },
  {
    id: 'rep-007',
    reportType: 'spam',
    reportedBy: 'Lorena Cabrera',
    reportedByUsername: '@lorenac_radio',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20mid%20twenties%20bright%20smile%20casual%20portrait%20clean%20background%20warm%20friendly%20lighting&width=120&height=120&seq=rep-lorena&orientation=squarish',
    reportedTarget: 'AutoPromo Fan',
    reportedTargetUsername: '@autopromo_fan',
    targetType: 'comentario-cabina',
    targetTypeLabel: 'Comentario en Cabina',
    reason: 'Autopromoción repetitiva de su propio canal en 3 programas distintos de la misma radio',
    evidenceSummary: 'Mismo mensaje autopromocional enviado en El Mañanero, Tardes Kalle y Noche Kalle',
    flowState: 'cerrado',
    assignedReviewer: 'Camila Ortega',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20friendly%20but%20authoritative%20expression%20clean%20modern%20studio%20portrait%20warm%20natural%20lighting&width=80&height=80&seq=rep-camila&orientation=squarish',
    resolution: 'advertencia-emitida',
    resolutionDetail: 'Advertencia formal emitida. Se explicó que la autopromoción debe hacerse en canales designados.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-19T17:45:00Z',
    createdAtRelative: 'Hace 2 días',
    updatedAt: '2026-06-19T18:30:00Z',
    closedAt: '2026-06-20T10:00:00Z',
    closedAtRelative: 'Ayer',
    context: 'La Kalle 96.3 FM · Cabina · El Mañanero, Tardes Kalle, Noche Kalle',
  },
  {
    id: 'rep-008',
    reportType: 'spam',
    reportedBy: 'Felipe Montero',
    reportedByUsername: '@felipemontero',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20casual%20relaxed%20expression%20clean%20portrait%20natural%20warm%20light&width=120&height=120&seq=rep-felipe-m&orientation=squarish',
    reportedTarget: 'Bots Club 2026',
    reportedTargetUsername: '@bots_club_26',
    targetType: 'club',
    targetTypeLabel: 'Club',
    reason: 'Cuenta sospechosa publicando mensajes automatizados cada 30 segundos en el club de fans',
    evidenceSummary: 'Patrón de publicación automática: 1 mensaje cada 30 segundos durante 2 horas con frases genéricas',
    flowState: 'en-revision',
    assignedReviewer: 'Natalia Romero',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20enthusiastic%20warm%20expression%20community-oriented%20vibe%20modern%20studio%20portrait&width=80&height=80&seq=rep-natalia&orientation=squarish',
    isRepeatOffender: true,
    repeatCount: 2,
    createdAt: '2026-06-21T09:20:00Z',
    createdAtRelative: 'Hace 52 min',
    updatedAt: '2026-06-21T09:35:00Z',
    context: 'Club Fanáticos La Kalle · Muro del Club',
  },

  // ═══════ INSULTOS (8 reportes) ═══════
  {
    id: 'rep-009',
    reportType: 'insultos',
    reportedBy: 'Paula Rodríguez',
    reportedByUsername: '@paula_rodri',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20concerned%20expression%20clean%20portrait%20warm%20lighting%20earnest%20look&width=120&height=120&seq=rep-paula&orientation=squarish',
    reportedTarget: 'Hater 47',
    reportedTargetUsername: '@hater_47',
    targetType: 'comentario-cabina',
    targetTypeLabel: 'Comentario en Cabina',
    reason: 'Insultos directos a la locutora Laura García sobre su forma de hablar durante El Show de la Mañana',
    evidenceSummary: 'Comentario: texto con lenguaje despectivo sobre el acento y la voz de la locutora',
    flowState: 'resuelto',
    assignedReviewer: 'Lucía Vargas',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20caring%20expression%20gentle%20but%20firm%20demeanor%20modern%20studio%20portrait%20warm%20soft%20lighting&width=80&height=80&seq=rep-lucia&orientation=squarish',
    resolution: 'usuario-silenciado',
    resolutionDetail: 'Silenciado por 48 horas. Primera ofensa por insultos.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T09:55:00Z',
    createdAtRelative: 'Hace 17 min',
    updatedAt: '2026-06-21T10:05:00Z',
    closedAt: '2026-06-21T10:05:00Z',
    closedAtRelative: 'Hace 7 min',
    context: 'Radio Felicidad 88.9 FM · Cabina · El Show de la Mañana',
  },
  {
    id: 'rep-010',
    reportType: 'insultos',
    reportedBy: 'Javier El Loco Ramírez',
    reportedByUsername: '@elloco_javier',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20animated%20expressive%20face%20clean%20portrait%20bright%20lighting%20passionate%20radio%20fan&width=120&height=120&seq=rep-javier-loco&orientation=squarish',
    reportedTarget: 'Troll Deportivo 88',
    reportedTargetUsername: '@troll_deportivo',
    targetType: 'post-publico',
    targetTypeLabel: 'Post Público',
    reason: 'Comentarios insultando a otros oyentes por preferir equipos contrarios en publicación de Carrusel Deportivo',
    evidenceSummary: '3 comentarios con insultos personales hacia oyentes que apoyan equipos rivales',
    flowState: 'resuelto',
    assignedReviewer: 'Diego Armando Paz',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20attentive%20expression%20modern%20clean%20look%20studio%20portrait%20balanced%20lighting&width=80&height=80&seq=rep-diego-paz&orientation=squarish',
    resolution: 'contenido-ocultado',
    resolutionDetail: 'Comentarios ocultados. Usuario advertido formalmente.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T08:45:00Z',
    createdAtRelative: 'Hace 1 hora',
    updatedAt: '2026-06-21T09:00:00Z',
    closedAt: '2026-06-21T09:00:00Z',
    closedAtRelative: 'Hace 1 hora',
    context: 'Onda Cero 104.5 FM · Feed Público · Post Carrusel Deportivo',
  },
  {
    id: 'rep-011',
    reportType: 'insultos',
    reportedBy: 'Sofía Hernández',
    reportedByUsername: '@sofia_oyente',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20earnest%20concerned%20expression%20clean%20portrait%20warm%20lighting%20attentive&width=120&height=120&seq=rep-sofia-h&orientation=squarish',
    reportedTarget: 'Agresivo 2026',
    reportedTargetUsername: '@agresivo_2026',
    targetType: 'mensaje-privado',
    targetTypeLabel: 'Mensaje Privado',
    reason: 'Mensajes privados con insultos y amenazas a un oyente que defendió a una locutora en la cabina',
    evidenceSummary: '5 mensajes privados con lenguaje agresivo, insultos personales y amenazas veladas',
    flowState: 'resuelto',
    assignedReviewer: 'Mateo Herrera',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20sharp%20focused%20expression%20neat%20appearance%20professional%20studio%20portrait&width=80&height=80&seq=rep-mateo&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo de 14 días. Segunda ofensa grave. Próxima será permanente.',
    isRepeatOffender: true,
    repeatCount: 2,
    createdAt: '2026-06-20T21:30:00Z',
    createdAtRelative: 'Ayer',
    updatedAt: '2026-06-20T21:50:00Z',
    closedAt: '2026-06-20T22:15:00Z',
    closedAtRelative: 'Ayer',
    context: 'Mensajes Privados · Remitente @agresivo_2026',
  },
  {
    id: 'rep-012',
    reportType: 'insultos',
    reportedBy: 'Ana María Cifuentes',
    reportedByUsername: '@anamaria_cif',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20early%20thirties%20concerned%20caring%20expression%20clean%20portrait%20soft%20lighting%20earnest&width=120&height=120&seq=rep-anamaria&orientation=squarish',
    reportedTarget: 'Lengua Venenosa',
    reportedTargetUsername: '@lengua_venenosa',
    targetType: 'club',
    targetTypeLabel: 'Club',
    reason: 'Insultos racistas hacia varios miembros del club de salsa por ser de regiones distintas',
    evidenceSummary: 'Comentarios con lenguaje discriminatorio basado en origen regional de los miembros',
    flowState: 'resuelto',
    assignedReviewer: 'Natalia Romero',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20enthusiastic%20warm%20expression%20community-oriented%20vibe%20modern%20studio%20portrait&width=80&height=80&seq=rep-natalia&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente del club y la radio. Reporte elevado a Super Admin.',
    isRepeatOffender: true,
    repeatCount: 4,
    createdAt: '2026-06-19T16:20:00Z',
    createdAtRelative: 'Hace 2 días',
    updatedAt: '2026-06-19T17:00:00Z',
    closedAt: '2026-06-20T09:00:00Z',
    closedAtRelative: 'Ayer',
    context: 'Salsa Brava Club · Chat del Club',
  },
  {
    id: 'rep-013',
    reportType: 'insultos',
    reportedBy: 'Ricardo Mendoza',
    reportedByUsername: '@ricardo_men_doza',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20twenties%20serious%20concerned%20expression%20clean%20portrait%20natural%20lighting%20earnest&width=120&height=120&seq=rep-ricardo-m&orientation=squarish',
    reportedTarget: 'Anónimo 772',
    reportedTargetUsername: '@anonimo_772',
    targetType: 'foto',
    targetTypeLabel: 'Foto',
    reason: 'Comentarios insultantes sobre la apariencia física de locutores en una foto de perfil del equipo',
    evidenceSummary: 'Serie de comentarios con insultos sobre la apariencia de 3 locutores en foto grupal',
    flowState: 'reportado',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T10:10:00Z',
    createdAtRelative: 'Hace 2 min',
    updatedAt: '2026-06-21T10:10:00Z',
    context: 'Rock & Pop 95.1 FM · Galería de Fotos · Foto del Equipo',
  },
  {
    id: 'rep-014',
    reportType: 'insultos',
    reportedBy: 'Mariana Restrepo',
    reportedByUsername: '@mariana_restrepo',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20protective%20concerned%20expression%20clean%20portrait%20soft%20lighting%20earnest%20look&width=120&height=120&seq=rep-mariana-r&orientation=squarish',
    reportedTarget: 'Machista 2025',
    reportedTargetUsername: '@machista_2025',
    targetType: 'comentario-cabina',
    targetTypeLabel: 'Comentario en Cabina',
    reason: 'Comentarios sexistas hacia la locutora Valentina durante segmento de entrevistas',
    evidenceSummary: 'Múltiples comentarios con lenguaje sexista y descalificaciones por género',
    flowState: 'resuelto',
    assignedReviewer: 'Gabriela Méndez',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20early%20thirties%20focused%20determined%20expression%20clean%20modern%20studio%20portrait%20strong%20approachable&width=80&height=80&seq=rep-gabriela&orientation=squarish',
    resolution: 'usuario-silenciado',
    resolutionDetail: 'Silenciado por 30 días. Primera ofensa de este tipo.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-20T15:30:00Z',
    createdAtRelative: 'Ayer',
    updatedAt: '2026-06-20T15:50:00Z',
    closedAt: '2026-06-20T16:10:00Z',
    closedAtRelative: 'Ayer',
    context: 'Tropicalísima 102.7 FM · Cabina · Tardes con Valentina',
  },
  {
    id: 'rep-015',
    reportType: 'insultos',
    reportedBy: 'Luis Fernando Gómez',
    reportedByUsername: '@luisfer_gomez',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20calm%20steady%20expression%20clean%20portrait%20natural%20light%20composed&width=120&height=120&seq=rep-luisfer&orientation=squarish',
    reportedTarget: 'Gritón Virtual',
    reportedTargetUsername: '@griton_virtual',
    targetType: 'programa',
    targetTypeLabel: 'Programa',
    reason: 'Insultos al conductor y a los panelistas durante un debate político en Noches de Rock',
    evidenceSummary: 'Comentarios en mayúsculas con insultos personales hacia conductor y 2 panelistas',
    flowState: 'en-revision',
    assignedReviewer: 'Julián Cárdenas',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20composed%20vigilant%20expression%20modern%20portrait%20clean%20studio%20lighting&width=80&height=80&seq=rep-julian&orientation=squarish',
    isRepeatOffender: true,
    repeatCount: 3,
    createdAt: '2026-06-21T01:30:00Z',
    createdAtRelative: 'Hace 9 horas',
    updatedAt: '2026-06-21T08:00:00Z',
    context: 'Rock & Pop 95.1 FM · Programa Noches de Rock · Cabina',
  },
  {
    id: 'rep-016',
    reportType: 'insultos',
    reportedBy: 'Camila Espinosa',
    reportedByUsername: '@camila_espinosa',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20earnest%20caring%20expression%20clean%20portrait%20warm%20natural%20light%20attentive&width=120&height=120&seq=rep-camila-e&orientation=squarish',
    reportedTarget: 'Furia En Linea',
    reportedTargetUsername: '@furia_en_linea',
    targetType: 'concurso',
    targetTypeLabel: 'Concurso',
    reason: 'Insultos a los ganadores del concurso Mejor Voz 2026 tras anunciarse los resultados',
    evidenceSummary: 'Varios comentarios insultando a los 3 ganadores con lenguaje despectivo sobre su talento',
    flowState: 'cerrado',
    assignedReviewer: 'Fernando El Juez',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20thirties%20impartial%20fair%20expression%20judge-like%20composure%20studio%20portrait%20balanced&width=80&height=80&seq=rep-fernando&orientation=squarish',
    resolution: 'contenido-eliminado',
    resolutionDetail: 'Comentarios eliminados. Usuario advertido sobre respeto en concursos.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-18T20:00:00Z',
    createdAtRelative: 'Hace 3 días',
    updatedAt: '2026-06-18T20:30:00Z',
    closedAt: '2026-06-19T12:00:00Z',
    closedAtRelative: 'Hace 2 días',
    context: 'Concursos RFW · Concurso Mejor Voz 2026 · Anuncio de Ganadores',
  },

  // ═══════ CONTENIDO OFENSIVO (8 reportes) ═══════
  {
    id: 'rep-017',
    reportType: 'contenido-ofensivo',
    reportedBy: 'Daniela Ortiz',
    reportedByUsername: '@daniela_ortiz',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20serious%20concerned%20expression%20clean%20portrait%20warm%20lighting%20protective&width=120&height=120&seq=rep-daniela-o&orientation=squarish',
    reportedTarget: 'DarkPoster RFW',
    reportedTargetUsername: '@darkposter_rfw',
    targetType: 'post-publico',
    targetTypeLabel: 'Post Público',
    reason: 'Publicación con imágenes violentas y texto que incita al odio en el feed comunitario',
    evidenceSummary: 'Post con imagen de contenido violento y texto promoviendo discriminación',
    flowState: 'resuelto',
    assignedReviewer: 'Santiago Rojas',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20serious%20composed%20expression%20professional%20clean%20appearance%20studio%20portrait%20cool%20lighting&width=80&height=80&seq=rep-santiago&orientation=squarish',
    resolution: 'contenido-eliminado',
    resolutionDetail: 'Contenido eliminado inmediatamente. Usuario bloqueado permanentemente. Reporte elevado.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T07:15:00Z',
    createdAtRelative: 'Hace 3 horas',
    updatedAt: '2026-06-21T07:22:00Z',
    closedAt: '2026-06-21T07:30:00Z',
    closedAtRelative: 'Hace 3 horas',
    context: 'Rock & Pop 95.1 FM · Feed Público',
  },
  {
    id: 'rep-018',
    reportType: 'contenido-ofensivo',
    reportedBy: 'Nicolás Castellanos',
    reportedByUsername: '@nicolas_cast',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20alert%20concerned%20expression%20clean%20portrait%20natural%20light%20protective&width=120&height=120&seq=rep-nicolas-c&orientation=squarish',
    reportedTarget: 'Memes Pesados',
    reportedTargetUsername: '@memes_pesados',
    targetType: 'club',
    targetTypeLabel: 'Club',
    reason: 'Publicación de memes con contenido sexual no consentido haciendo referencia a locutoras',
    evidenceSummary: '3 memes con imágenes editadas de locutoras en contextos sexualizados sin consentimiento',
    flowState: 'resuelto',
    assignedReviewer: 'Natalia Romero',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20enthusiastic%20warm%20expression%20community-oriented%20vibe%20modern%20studio%20portrait&width=80&height=80&seq=rep-natalia&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente. Contenido eliminado. Caso documentado para posible acción legal.',
    isRepeatOffender: true,
    repeatCount: 2,
    createdAt: '2026-06-20T19:45:00Z',
    createdAtRelative: 'Ayer',
    updatedAt: '2026-06-20T20:15:00Z',
    closedAt: '2026-06-20T20:15:00Z',
    closedAtRelative: 'Ayer',
    context: 'Club Fanáticos La Kalle · Muro del Club',
  },
  {
    id: 'rep-019',
    reportType: 'contenido-ofensivo',
    reportedBy: 'Valeria Sánchez',
    reportedByUsername: '@valeria_san',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20disturbed%20concerned%20expression%20clean%20portrait%20soft%20lighting%20protective%20earnest&width=120&height=120&seq=rep-valeria-s&orientation=squarish',
    reportedTarget: 'Cuenta Fantasma 404',
    reportedTargetUsername: '@fantasma_404',
    targetType: 'foto',
    targetTypeLabel: 'Foto',
    reason: 'Foto de perfil con simbología de grupos de odio y mensaje amenazante en la descripción',
    evidenceSummary: 'Foto de perfil con símbolos asociados a grupos extremistas y biografía con amenazas',
    flowState: 'resuelto',
    assignedReviewer: 'Santiago Rojas',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20serious%20composed%20expression%20professional%20clean%20appearance%20studio%20portrait%20cool%20lighting&width=80&height=80&seq=rep-santiago&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Cuenta suspendida permanentemente. IP registrada. Reporte a autoridades.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T06:00:00Z',
    createdAtRelative: 'Hace 4 horas',
    updatedAt: '2026-06-21T06:15:00Z',
    closedAt: '2026-06-21T06:30:00Z',
    closedAtRelative: 'Hace 4 horas',
    context: 'Perfil de Usuario · @fantasma_404',
  },
  {
    id: 'rep-020',
    reportType: 'contenido-ofensivo',
    reportedBy: 'Esteban Cardona',
    reportedByUsername: '@esteban_cardona',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20serious%20protective%20expression%20clean%20portrait%20natural%20lighting%20composed&width=120&height=120&seq=rep-esteban-c&orientation=squarish',
    reportedTarget: 'GoreSender 666',
    reportedTargetUsername: '@goresender_666',
    targetType: 'comentario-cabina',
    targetTypeLabel: 'Comentario en Cabina',
    reason: 'Envío de enlaces con contenido gráfico violento en el chat durante programa matutino',
    evidenceSummary: 'Enlace a sitio externo con contenido gráfico de violencia explícita enviado en cabina',
    flowState: 'resuelto',
    assignedReviewer: 'Mateo Herrera',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20sharp%20focused%20expression%20neat%20appearance%20professional%20studio%20portrait&width=80&height=80&seq=rep-mateo&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente inmediato. Enlace reportado a plataformas externas.',
    isRepeatOffender: true,
    repeatCount: 2,
    createdAt: '2026-06-19T08:30:00Z',
    createdAtRelative: 'Hace 2 días',
    updatedAt: '2026-06-19T08:35:00Z',
    closedAt: '2026-06-19T08:40:00Z',
    closedAtRelative: 'Hace 2 días',
    context: 'Onda Cero 104.5 FM · Cabina · Matinal',
  },
  {
    id: 'rep-021',
    reportType: 'contenido-ofensivo',
    reportedBy: 'Isabel Cristina Mora',
    reportedByUsername: '@isabel_mora',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20late%20twenties%20serious%20protective%20expression%20clean%20portrait%20warm%20lighting%20determined&width=120&height=120&seq=rep-isabel-m&orientation=squarish',
    reportedTarget: 'Xenófobo 2026',
    reportedTargetUsername: '@xenofobo_2026',
    targetType: 'club',
    targetTypeLabel: 'Club',
    reason: 'Contenido xenófobo contra oyentes de otros países en el foro del club de música internacional',
    evidenceSummary: 'Varios posts con lenguaje despectivo hacia oyentes de países vecinos en club de World Music',
    flowState: 'en-revision',
    assignedReviewer: 'Isabella Cruz',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20mid%20twenties%20bright%20inclusive%20expression%20community%20builder%20modern%20studio%20portrait%20warm%20engaging&width=80&height=80&seq=rep-isabella&orientation=squarish',
    isRepeatOffender: true,
    repeatCount: 3,
    createdAt: '2026-06-21T09:00:00Z',
    createdAtRelative: 'Hace 1 hora',
    updatedAt: '2026-06-21T09:10:00Z',
    context: 'World Music Club · Foro del Club',
  },
  {
    id: 'rep-022',
    reportType: 'contenido-ofensivo',
    reportedBy: 'Manuela García',
    reportedByUsername: '@manuela_garcia',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20serious%20concerned%20expression%20clean%20portrait%20soft%20lighting%20attentive%20protective&width=120&height=120&seq=rep-manuela-g&orientation=squarish',
    reportedTarget: 'VideoDañino',
    reportedTargetUsername: '@videodañino',
    targetType: 'video',
    targetTypeLabel: 'Video',
    reason: 'Video con contenido perturbador subido a la galería comunitaria simulando ser un clip musical',
    evidenceSummary: 'Video con contenido perturbador disfrazado de video musical. Miniatura engañosa.',
    flowState: 'reportado',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T10:05:00Z',
    createdAtRelative: 'Hace 7 min',
    updatedAt: '2026-06-21T10:05:00Z',
    context: 'Tropicalísima 102.7 FM · Galería de Videos',
  },
  {
    id: 'rep-023',
    reportType: 'contenido-ofensivo',
    reportedBy: 'Alejandro Restrepo',
    reportedByUsername: '@ale_restrepo',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20twenties%20earnest%20concerned%20expression%20clean%20portrait%20natural%20lighting%20protective&width=120&height=120&seq=rep-ale-r&orientation=squarish',
    reportedTarget: 'Discriminador N1',
    reportedTargetUsername: '@discriminador_n1',
    targetType: 'evento',
    targetTypeLabel: 'Evento',
    reason: 'Comentarios discriminatorios sobre accesibilidad en el foro de los Premios RFW',
    evidenceSummary: 'Comentarios burlándose de la solicitud de subtítulos y accesibilidad para personas con discapacidad',
    flowState: 'cerrado',
    assignedReviewer: 'Paola Andrea Díaz',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20mid%20twenties%20alert%20organized%20expression%20event%20coordinator%20style%20studio%20portrait%20clean%20modern&width=80&height=80&seq=rep-paola&orientation=squarish',
    resolution: 'usuario-silenciado',
    resolutionDetail: 'Silenciado por 14 días. RFW reafirma su compromiso con la inclusión.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-17T11:00:00Z',
    createdAtRelative: 'Hace 4 días',
    updatedAt: '2026-06-17T11:45:00Z',
    closedAt: '2026-06-18T09:00:00Z',
    closedAtRelative: 'Hace 3 días',
    context: 'Premios RFW 2026 · Foro del Evento',
  },
  {
    id: 'rep-024',
    reportType: 'contenido-ofensivo',
    reportedBy: 'Laura Patricia Mejía',
    reportedByUsername: '@laurap_mejia',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20early%20thirties%20serious%20caring%20expression%20clean%20portrait%20soft%20lighting%20protective%20determined&width=120&height=120&seq=rep-laurap&orientation=squarish',
    reportedTarget: 'Vendedor Humo',
    reportedTargetUsername: '@vendedor_humo',
    targetType: 'post-publico',
    targetTypeLabel: 'Post Público',
    reason: 'Publicación con desinformación médica peligrosa en el feed de la radio de salud',
    evidenceSummary: 'Post promoviendo tratamientos no comprobados y potencialmente peligrosos para enfermedades graves',
    flowState: 'reportado',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T10:15:00Z',
    createdAtRelative: 'Ahora',
    updatedAt: '2026-06-21T10:15:00Z',
    context: 'Radio Felicidad 88.9 FM · Feed Público',
  },

  // ═══════ PUBLICIDAD NO AUTORIZADA (8 reportes) ═══════
  {
    id: 'rep-025',
    reportType: 'publicidad-no-autorizada',
    reportedBy: 'Andrés Felipe Castro',
    reportedByUsername: '@andres_mod',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20professional%20expression%20tidy%20modern%20style%20studio%20portrait%20balanced%20lighting&width=120&height=120&seq=rep-andres-c&orientation=squarish',
    reportedTarget: 'Vendedor Oficial Falso',
    reportedTargetUsername: '@vendedor_oficial',
    targetType: 'post-publico',
    targetTypeLabel: 'Post Público',
    reason: 'Publicación haciéndose pasar por patrocinador oficial vendiendo productos sin autorización de La Mega',
    evidenceSummary: 'Post con logos de La Mega y RFW sin autorización ofreciendo descuentos falsos en productos',
    flowState: 'resuelto',
    assignedReviewer: 'Andrés Felipe Castro',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20professional%20expression%20tidy%20modern%20style%20studio%20portrait%20balanced%20lighting&width=80&height=80&seq=rep-andres-c&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente. Post eliminado. Verificación de IP para prevenir reincidencia.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T09:30:00Z',
    createdAtRelative: 'Hace 42 min',
    updatedAt: '2026-06-21T09:45:00Z',
    closedAt: '2026-06-21T09:45:00Z',
    closedAtRelative: 'Hace 27 min',
    context: 'La Mega 107.3 FM · Feed Público',
  },
  {
    id: 'rep-026',
    reportType: 'publicidad-no-autorizada',
    reportedBy: 'Juliana Estrada',
    reportedByUsername: '@juliana_estrada',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20observant%20sharp%20expression%20clean%20portrait%20warm%20lighting%20attentive&width=120&height=120&seq=rep-juliana-e&orientation=squarish',
    reportedTarget: 'Radio Pirata FM',
    reportedTargetUsername: '@radio_pirata_fm',
    targetType: 'comentario-cabina',
    targetTypeLabel: 'Comentario en Cabina',
    reason: 'Promoción de otra radio FM no afiliada a RFW en la cabina de Onda Cero durante programa estelar',
    evidenceSummary: 'Múltiples mensajes diciendo "mejor escuchen Radio Pirata 99.9 FM, es mucho mejor"',
    flowState: 'resuelto',
    assignedReviewer: 'Mateo Herrera',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20sharp%20focused%20expression%20neat%20appearance%20professional%20studio%20portrait&width=80&height=80&seq=rep-mateo&orientation=squarish',
    resolution: 'usuario-silenciado',
    resolutionDetail: 'Silenciado 7 días. Primera ofensa. Se explicaron las normas de competencia leal.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-20T17:45:00Z',
    createdAtRelative: 'Ayer',
    updatedAt: '2026-06-20T18:00:00Z',
    closedAt: '2026-06-20T18:00:00Z',
    closedAtRelative: 'Ayer',
    context: 'Onda Cero 104.5 FM · Cabina · Programa Estelar',
  },
  {
    id: 'rep-027',
    reportType: 'publicidad-no-autorizada',
    reportedBy: 'Felipe Torres R',
    reportedByUsername: '@felipetorres_r',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20alert%20observant%20expression%20clean%20portrait%20professional%20lighting%20attentive&width=120&height=120&seq=rep-felipe-t&orientation=squarish',
    reportedTarget: 'Marca Colada',
    reportedTargetUsername: '@marca_colada',
    targetType: 'club',
    targetTypeLabel: 'Club',
    reason: 'Promoción encubierta de una marca de bebidas en posts del club disfrazados de contenido orgánico',
    evidenceSummary: '5 posts con menciones sutiles repetidas a una marca de bebidas energéticas sin declarar publicidad',
    flowState: 'en-revision',
    assignedReviewer: 'Natalia Romero',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20enthusiastic%20warm%20expression%20community-oriented%20vibe%20modern%20studio%20portrait&width=80&height=80&seq=rep-natalia&orientation=squarish',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T08:10:00Z',
    createdAtRelative: 'Hace 2 horas',
    updatedAt: '2026-06-21T08:30:00Z',
    context: 'Club Rockeros Unidos · Muro del Club',
  },
  {
    id: 'rep-028',
    reportType: 'publicidad-no-autorizada',
    reportedBy: 'Marcela Jiménez',
    reportedByUsername: '@marcela_jime',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20mid%20twenties%20sharp%20attentive%20expression%20clean%20portrait%20professional%20lighting%20observant&width=120&height=120&seq=rep-marcela-j&orientation=squarish',
    reportedTarget: 'Curso Milagroso',
    reportedTargetUsername: '@curso_milagroso',
    targetType: 'mensaje-privado',
    targetTypeLabel: 'Mensaje Privado',
    reason: 'Envío de mensajes privados ofreciendo cursos pagos de locución usando el nombre de RFW sin autorización',
    evidenceSummary: 'Mensaje privado ofreciendo "Curso Oficial RFW de Locución" por $200 USD sin afiliación real',
    flowState: 'resuelto',
    assignedReviewer: 'Camila Ortega',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20friendly%20but%20authoritative%20expression%20clean%20modern%20studio%20portrait%20warm%20natural%20lighting&width=80&height=80&seq=rep-camila&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente. Uso no autorizado de marca RFW. Posible acción legal.',
    isRepeatOffender: true,
    repeatCount: 2,
    createdAt: '2026-06-19T14:00:00Z',
    createdAtRelative: 'Hace 2 días',
    updatedAt: '2026-06-19T14:30:00Z',
    closedAt: '2026-06-19T15:00:00Z',
    closedAtRelative: 'Hace 2 días',
    context: 'Mensajes Privados · 8 destinatarios',
  },
  {
    id: 'rep-029',
    reportType: 'publicidad-no-autorizada',
    reportedBy: 'Santiago González',
    reportedByUsername: '@santi_gonzalez',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20alert%20sharp%20expression%20clean%20portrait%20natural%20lighting%20observant&width=120&height=120&seq=rep-santi-g&orientation=squarish',
    reportedTarget: 'Patrocinador Impostor',
    reportedTargetUsername: '@patrocinador_fake',
    targetType: 'evento',
    targetTypeLabel: 'Evento',
    reason: 'Comentarios ofreciendo pauta publicitaria falsa en la página del RFW Fest a radios asistentes',
    evidenceSummary: 'Comentario ofreciendo espacios publicitarios falsos en el RFW Fest a radios participantes',
    flowState: 'reportado',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T10:12:00Z',
    createdAtRelative: 'Ahora',
    updatedAt: '2026-06-21T10:12:00Z',
    context: 'RFW Fest 2026 Medellín · Página del Evento',
  },
  {
    id: 'rep-030',
    reportType: 'publicidad-no-autorizada',
    reportedBy: 'Carolina Meza',
    reportedByUsername: '@caro_meza',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20observant%20attentive%20expression%20clean%20portrait%20warm%20lighting%20sharp&width=120&height=120&seq=rep-caro-m&orientation=squarish',
    reportedTarget: 'Tienda No Oficial',
    reportedTargetUsername: '@tienda_no_oficial',
    targetType: 'post-publico',
    targetTypeLabel: 'Post Público',
    reason: 'Venta de mercancía no oficial con logos de RFW y radios afiliadas sin licencia',
    evidenceSummary: 'Post ofreciendo camisetas y gorras con logos de RFW y 5 radios sin autorización',
    flowState: 'cerrado',
    assignedReviewer: 'Valentina Suárez',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20mid%20twenties%20gentle%20smile%20alert%20eyes%20modern%20studio%20portrait%20clean%20background&width=80&height=80&seq=rep-valentina&orientation=squarish',
    resolution: 'contenido-eliminado',
    resolutionDetail: 'Contenido eliminado. Usuario advertido sobre propiedad intelectual. Caso cerrado.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-16T10:00:00Z',
    createdAtRelative: 'Hace 5 días',
    updatedAt: '2026-06-16T11:00:00Z',
    closedAt: '2026-06-17T08:00:00Z',
    closedAtRelative: 'Hace 4 días',
    context: 'Tropicalísima 102.7 FM · Feed Público',
  },
  {
    id: 'rep-031',
    reportType: 'publicidad-no-autorizada',
    reportedBy: 'Pedro Nel Ramírez',
    reportedByUsername: '@pedronel_r',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20attentive%20alert%20expression%20clean%20portrait%20professional%20lighting%20observant&width=120&height=120&seq=rep-pedronel&orientation=squarish',
    reportedTarget: 'AdsBot Pro',
    reportedTargetUsername: '@adsbot_pro',
    targetType: 'comentario-cabina',
    targetTypeLabel: 'Comentario en Cabina',
    reason: 'Bot publicitario automatizado publicando anuncios de apuestas cada 5 minutos en la cabina',
    evidenceSummary: 'Patrón automatizado: mismo mensaje publicitario cada 5 minutos exactos durante 3 horas',
    flowState: 'resuelto',
    assignedReviewer: 'Gabriela Méndez',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20early%20thirties%20focused%20determined%20expression%20clean%20modern%20studio%20portrait%20strong%20approachable&width=80&height=80&seq=rep-gabriela&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Cuenta bloqueada permanentemente. Confirmado como bot por patrón de tiempo.',
    isRepeatOffender: true,
    repeatCount: 4,
    createdAt: '2026-06-21T07:00:00Z',
    createdAtRelative: 'Hace 3 horas',
    updatedAt: '2026-06-21T07:20:00Z',
    closedAt: '2026-06-21T07:20:00Z',
    closedAtRelative: 'Hace 3 horas',
    context: 'La Kalle 96.3 FM · Cabina · El Mañanero',
  },
  {
    id: 'rep-032',
    reportType: 'publicidad-no-autorizada',
    reportedBy: 'Tatiana López',
    reportedByUsername: '@tatiana_lop',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20sharp%20observant%20expression%20clean%20portrait%20warm%20lighting%20attentive%20professional&width=120&height=120&seq=rep-tatiana-l&orientation=squarish',
    reportedTarget: 'Influencer Falso',
    reportedTargetUsername: '@influencer_falso',
    targetType: 'perfil-usuario',
    targetTypeLabel: 'Perfil de Usuario',
    reason: 'Perfil falso de influencer promocionando productos de dudosa procedencia en el feed comunitario',
    evidenceSummary: 'Perfil con foto robada de influencer real promocionando suplementos no regulados',
    flowState: 'en-revision',
    assignedReviewer: 'Santiago Rojas',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20serious%20composed%20expression%20professional%20clean%20appearance%20studio%20portrait%20cool%20lighting&width=80&height=80&seq=rep-santiago&orientation=squarish',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T09:25:00Z',
    createdAtRelative: 'Hace 47 min',
    updatedAt: '2026-06-21T09:40:00Z',
    context: 'Comunidad RFW · Perfil de Usuario · @influencer_falso',
  },

  // ═══════ SUPLANTACIÓN (8 reportes) ═══════
  {
    id: 'rep-033',
    reportType: 'suplantacion',
    reportedBy: 'Ricky Navarro',
    reportedByUsername: '@ricky_navarro_oficial',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20thirties%20charismatic%20radio%20host%20expression%20professional%20studio%20portrait%20warm%20lighting%20confident&width=120&height=120&seq=rep-ricky-nav&orientation=squarish',
    reportedTarget: 'Falso Ricky Navarro',
    reportedTargetUsername: '@ricky_navarro_rp',
    targetType: 'perfil-usuario',
    targetTypeLabel: 'Perfil de Usuario',
    reason: 'Cuenta haciéndose pasar por mí (Ricky Navarro) pidiendo dinero a los oyentes para supuestas campañas',
    evidenceSummary: 'Perfil con foto, nombre y bio copiados del locutor real. Solicitando transferencias.',
    flowState: 'resuelto',
    assignedReviewer: 'Santiago Rojas',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20serious%20composed%20expression%20professional%20clean%20appearance%20studio%20portrait%20cool%20lighting&width=80&height=80&seq=rep-santiago&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente. IP registrada. Caso elevado a autoridades por intento de estafa.',
    isRepeatOffender: true,
    repeatCount: 2,
    createdAt: '2026-06-21T09:00:00Z',
    createdAtRelative: 'Hace 1 hora',
    updatedAt: '2026-06-21T09:15:00Z',
    closedAt: '2026-06-21T09:30:00Z',
    closedAtRelative: 'Hace 42 min',
    context: 'Rock & Pop 95.1 FM · Perfil de Usuario · @ricky_navarro_rp',
  },
  {
    id: 'rep-034',
    reportType: 'suplantacion',
    reportedBy: 'Laura García',
    reportedByUsername: '@laura_garcia_oficial',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20early%20thirties%20warm%20charismatic%20radio%20host%20expression%20professional%20studio%20portrait%20natural%20lighting%20confident&width=120&height=120&seq=rep-laura-g&orientation=squarish',
    reportedTarget: 'Laura Garcia Fan',
    reportedTargetUsername: '@laura_garcia_fans',
    targetType: 'perfil-usuario',
    targetTypeLabel: 'Perfil de Usuario',
    reason: 'Perfil usando mi nombre y foto para crear un club de fans falso que cobra membresía',
    evidenceSummary: 'Perfil con nombre, foto y bio de la locutora. Cobrando $5 USD por unirse al "Club Oficial".',
    flowState: 'resuelto',
    assignedReviewer: 'Lucía Vargas',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20caring%20expression%20gentle%20but%20firm%20demeanor%20modern%20studio%20portrait%20warm%20soft%20lighting&width=80&height=80&seq=rep-lucia&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente. Perfil eliminado. Miembros del club falso notificados.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-20T16:00:00Z',
    createdAtRelative: 'Ayer',
    updatedAt: '2026-06-20T16:30:00Z',
    closedAt: '2026-06-20T16:45:00Z',
    closedAtRelative: 'Ayer',
    context: 'Radio Felicidad 88.9 FM · Perfil de Usuario · @laura_garcia_fans',
  },
  {
    id: 'rep-035',
    reportType: 'suplantacion',
    reportedBy: 'Juan Pablo Cárdenas',
    reportedByUsername: '@juanpablo_card',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20concerned%20protective%20expression%20clean%20portrait%20natural%20lighting%20earnest&width=120&height=120&seq=rep-juanpablo&orientation=squarish',
    reportedTarget: 'RFW Soporte Falso',
    reportedTargetUsername: '@rfw_soporte_ayuda',
    targetType: 'mensaje-privado',
    targetTypeLabel: 'Mensaje Privado',
    reason: 'Cuenta que se hace pasar por Soporte Oficial de RFW pidiendo contraseñas por mensaje privado',
    evidenceSummary: 'Mensaje privado con tono oficial de RFW solicitando "verificación de cuenta" con usuario y contraseña',
    flowState: 'resuelto',
    assignedReviewer: 'Mateo Herrera',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20sharp%20focused%20expression%20neat%20appearance%20professional%20studio%20portrait&width=80&height=80&seq=rep-mateo&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente. Alerta enviada a todos los usuarios que recibieron el mensaje.',
    isRepeatOffender: true,
    repeatCount: 2,
    createdAt: '2026-06-21T08:00:00Z',
    createdAtRelative: 'Hace 2 horas',
    updatedAt: '2026-06-21T08:20:00Z',
    closedAt: '2026-06-21T08:25:00Z',
    closedAtRelative: 'Hace 2 horas',
    context: 'Mensajes Privados · Suplantación de Soporte RFW',
  },
  {
    id: 'rep-036',
    reportType: 'suplantacion',
    reportedBy: 'María José Vélez',
    reportedByUsername: '@mariajose_v',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20suspicious%20concerned%20expression%20clean%20portrait%20warm%20lighting%20protective%20attentive&width=120&height=120&seq=rep-mariajose&orientation=squarish',
    reportedTarget: 'Moderador Falso RFW',
    reportedTargetUsername: '@mod_rfw_oficial',
    targetType: 'perfil-usuario',
    targetTypeLabel: 'Perfil de Usuario',
    reason: 'Perfil haciéndose pasar por moderador de RFW amenazando con banear usuarios si no comparten contenido',
    evidenceSummary: 'Perfil con insignia de moderador falsa pidiendo compartir posts bajo amenaza de ban',
    flowState: 'resuelto',
    assignedReviewer: 'Camila Ortega',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20friendly%20but%20authoritative%20expression%20clean%20modern%20studio%20portrait%20warm%20natural%20lighting&width=80&height=80&seq=rep-camila&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente. Insignia falsa removida. Alerta comunitaria emitida.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-19T13:30:00Z',
    createdAtRelative: 'Hace 2 días',
    updatedAt: '2026-06-19T14:00:00Z',
    closedAt: '2026-06-19T14:15:00Z',
    closedAtRelative: 'Hace 2 días',
    context: 'Comunidad RFW · Perfil de Usuario · @mod_rfw_oficial',
  },
  {
    id: 'rep-037',
    reportType: 'suplantacion',
    reportedBy: 'El Podcast de Pedro',
    reportedByUsername: '@podcast_pedro',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20creative%20podcaster%20expression%20clean%20portrait%20professional%20lighting%20concerned%20earnest&width=120&height=120&seq=rep-pedro-pod&orientation=squarish',
    reportedTarget: 'Pedro Podcast Fake',
    reportedTargetUsername: '@podcast_pedro_ok',
    targetType: 'perfil-usuario',
    targetTypeLabel: 'Perfil de Usuario',
    reason: 'Cuenta duplicada de mi podcast publicando contenido de baja calidad con mi nombre',
    evidenceSummary: 'Perfil con nombre casi idéntico (un guión bajo de diferencia) publicando contenido ajeno',
    flowState: 'reportado',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T10:18:00Z',
    createdAtRelative: 'Ahora',
    updatedAt: '2026-06-21T10:18:00Z',
    context: 'Comunidad RFW · Perfil de Usuario · @podcast_pedro_ok',
  },
  {
    id: 'rep-038',
    reportType: 'suplantacion',
    reportedBy: 'Diego Fernando Londoño',
    reportedByUsername: '@diegolondono',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20twenties%20serious%20protective%20expression%20clean%20portrait%20professional%20lighting%20concerned&width=120&height=120&seq=rep-diego-l&orientation=squarish',
    reportedTarget: 'La Kalle Soporte',
    reportedTargetUsername: '@lakalle_soporte',
    targetType: 'comentario-cabina',
    targetTypeLabel: 'Comentario en Cabina',
    reason: 'Cuenta haciéndose pasar por staff de La Kalle pidiendo datos personales en la cabina',
    evidenceSummary: 'Comentario en cabina: "Soy del equipo de La Kalle, necesito verificar tu número de teléfono para el sorteo"',
    flowState: 'resuelto',
    assignedReviewer: 'Camila Ortega',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20woman%20friendly%20but%20authoritative%20expression%20clean%20modern%20studio%20portrait%20warm%20natural%20lighting&width=80&height=80&seq=rep-camila&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente. Alerta en cabina avisando que La Kalle nunca pide datos por chat.',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-20T22:10:00Z',
    createdAtRelative: 'Ayer',
    updatedAt: '2026-06-20T22:25:00Z',
    closedAt: '2026-06-20T22:30:00Z',
    closedAtRelative: 'Ayer',
    context: 'La Kalle 96.3 FM · Cabina · Programa Nocturno',
  },
  {
    id: 'rep-039',
    reportType: 'suplantacion',
    reportedBy: 'Camilo Torres',
    reportedByUsername: '@camilo_torres',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20mid%20twenties%20observant%20sharp%20expression%20clean%20portrait%20natural%20lighting%20attentive%20concerned&width=120&height=120&seq=rep-camilo-t&orientation=squarish',
    reportedTarget: 'Ganador Falso Copa',
    reportedTargetUsername: '@ganador_copa_rfw',
    targetType: 'concurso',
    targetTypeLabel: 'Concurso',
    reason: 'Perfil suplantando a un ganador de la Copa RFW ofreciendo "entradas VIP" a cambio de dinero',
    evidenceSummary: 'Perfil con nombre y foto de ganador real de la Copa RFW 2026 ofreciendo entradas falsas',
    flowState: 'en-revision',
    assignedReviewer: 'Fernando El Juez',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20late%20thirties%20impartial%20fair%20expression%20judge-like%20composure%20studio%20portrait%20balanced&width=80&height=80&seq=rep-fernando&orientation=squarish',
    isRepeatOffender: false,
    repeatCount: 1,
    createdAt: '2026-06-21T09:45:00Z',
    createdAtRelative: 'Hace 27 min',
    updatedAt: '2026-06-21T09:50:00Z',
    context: 'Copa RFW 2026 · Página del Evento',
  },
  {
    id: 'rep-040',
    reportType: 'suplantacion',
    reportedBy: 'Patricia Elena Ríos',
    reportedByUsername: '@patricia_rios',
    reportedByPhotoUrl: 'https://readdy.ai/api/search-image?query=Latina%20woman%20early%20thirties%20protective%20concerned%20expression%20clean%20portrait%20soft%20lighting%20earnest%20attentive&width=120&height=120&seq=rep-patricia-r&orientation=squarish',
    reportedTarget: 'Embajador RFW Fake',
    reportedTargetUsername: '@embajador_rfw_fake',
    targetType: 'perfil-usuario',
    targetTypeLabel: 'Perfil de Usuario',
    reason: 'Cuenta falsa de Embajador RFW ofreciendo beneficios inexistentes a cambio de "cuota de afiliación"',
    evidenceSummary: 'Perfil con insignia de Embajador falsa cobrando por beneficios que son gratuitos para embajadores reales',
    flowState: 'cerrado',
    assignedReviewer: 'Santiago Rojas',
    assignedReviewerPhotoUrl: 'https://readdy.ai/api/search-image?query=Latino%20man%20early%20thirties%20serious%20composed%20expression%20professional%20clean%20appearance%20studio%20portrait%20cool%20lighting&width=80&height=80&seq=rep-santiago&orientation=squarish',
    resolution: 'usuario-bloqueado',
    resolutionDetail: 'Bloqueo permanente. Todos los embajadores reales notificados. Comunidad advertida.',
    isRepeatOffender: true,
    repeatCount: 3,
    createdAt: '2026-06-15T09:00:00Z',
    createdAtRelative: 'Hace 6 días',
    updatedAt: '2026-06-15T09:45:00Z',
    closedAt: '2026-06-16T10:00:00Z',
    closedAtRelative: 'Hace 5 días',
    context: 'Comunidad RFW · Perfil de Usuario · @embajador_rfw_fake',
  },
];

export const reincidencias: ReincidenciaEntry[] = [
  {
    id: 'reinc-1',
    reportedTargetUsername: '@spammer_rfw2026',
    reportedTargetPhotoUrl: 'https://readdy.ai/api/search-image?query=Generic%20anonymous%20avatar%20dark%20silhouette%20simple%20circle%20icon%20neutral%20gray%20background%20placeholder&width=120&height=120&seq=reinc-1&orientation=squarish',
    totalReports: 7,
    reportTypes: ['spam', 'publicidad-no-autorizada'],
    lastReportDate: '2026-06-21',
    lastReportDateRelative: 'Hoy',
    status: 'reincidente',
    statusLabel: 'Reincidente',
    previousSanctions: ['Silencio 3 días (Jun 15)', 'Silencio 7 días (Jun 18)', 'Bloqueo 14 días (Jun 20)'],
  },
  {
    id: 'reinc-2',
    reportedTargetUsername: '@lengua_venenosa',
    reportedTargetPhotoUrl: 'https://readdy.ai/api/search-image?query=Dark%20generic%20anonymous%20user%20profile%20silhouette%20neutral%20circular%20avatar%20simple%20icon%20placeholder&width=120&height=120&seq=reinc-2&orientation=squarish',
    totalReports: 4,
    reportTypes: ['insultos', 'contenido-ofensivo'],
    lastReportDate: '2026-06-19',
    lastReportDateRelative: 'Hace 2 días',
    status: 'sancionado',
    statusLabel: 'Sancionado',
    previousSanctions: ['Advertencia (May 20)', 'Silencio 14 días (Jun 1)', 'Bloqueo Permanente (Jun 19)'],
  },
  {
    id: 'reinc-3',
    reportedTargetUsername: '@vendecuentas_pro',
    reportedTargetPhotoUrl: 'https://readdy.ai/api/search-image?query=Simple%20anonymous%20avatar%20circle%20dark%20silhouette%20generic%20placeholder%20profile%20icon%20neutral%20minimal%20flat&width=120&height=120&seq=reinc-3&orientation=squarish',
    totalReports: 5,
    reportTypes: ['spam'],
    lastReportDate: '2026-06-21',
    lastReportDateRelative: 'Hoy',
    status: 'sancionado',
    statusLabel: 'Sancionado',
    previousSanctions: ['Advertencia (Abr 10)', 'Silencio 7 días (May 22)', 'Bloqueo 30 días (Jun 5)', 'Bloqueo Permanente (Jun 21)'],
  },
  {
    id: 'reinc-4',
    reportedTargetUsername: '@adsbot_pro',
    reportedTargetPhotoUrl: 'https://readdy.ai/api/search-image?query=Anonymous%20user%20avatar%20silhouette%20dark%20circle%20minimal%20placeholder%20generic%20profile%20icon%20neutral%20background&width=120&height=120&seq=reinc-4&orientation=squarish',
    totalReports: 4,
    reportTypes: ['publicidad-no-autorizada', 'spam'],
    lastReportDate: '2026-06-21',
    lastReportDateRelative: 'Hace 3 horas',
    status: 'sancionado',
    statusLabel: 'Sancionado',
    previousSanctions: ['Silencio 3 días (Jun 12)', 'Bloqueo 7 días (Jun 16)', 'Bloqueo Permanente (Jun 21)'],
  },
  {
    id: 'reinc-5',
    reportedTargetUsername: '@griton_virtual',
    reportedTargetPhotoUrl: 'https://readdy.ai/api/search-image?query=Generic%20anonymous%20user%20silhouette%20circle%20icon%20simple%20neutral%20gray%20background%20placeholder%20avatar%20minimal&width=120&height=120&seq=reinc-5&orientation=squarish',
    totalReports: 3,
    reportTypes: ['insultos'],
    lastReportDate: '2026-06-21',
    lastReportDateRelative: 'Hace 9 horas',
    status: 'vigilancia',
    statusLabel: 'En Vigilancia',
    previousSanctions: ['Advertencia (Jun 10)', 'Silencio 48 horas (Jun 15)'],
  },
  {
    id: 'reinc-6',
    reportedTargetUsername: '@xenofobo_2026',
    reportedTargetPhotoUrl: 'https://readdy.ai/api/search-image?query=Plain%20generic%20user%20avatar%20silhouette%20neutral%20gray%20circle%20minimal%20anonymous%20profile%20placeholder%20flat%20design&width=120&height=120&seq=reinc-6&orientation=squarish',
    totalReports: 3,
    reportTypes: ['contenido-ofensivo', 'insultos'],
    lastReportDate: '2026-06-21',
    lastReportDateRelative: 'Hace 1 hora',
    status: 'vigilancia',
    statusLabel: 'En Vigilancia',
    previousSanctions: ['Advertencia (May 28)', 'Silencio 72 horas (Jun 8)'],
  },
];

export const reportRules = [
  {
    id: 1,
    title: 'No eliminar automáticamente',
    description: 'Ningún contenido reportado se elimina de forma automática. Cada reporte debe ser revisado por un moderador humano que evalúe el contexto, la intención y la gravedad antes de tomar cualquier acción. La automatización castiga sin entender; la moderación humana juzga con criterio.',
    icon: 'ri-eye-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  {
    id: 2,
    title: 'Flujo completo y trazable',
    description: 'Cada reporte sigue cuatro etapas: Reportar (el usuario envía la denuncia), Revisar (un moderador analiza el caso), Resolver (se toma una acción proporcional) y Cerrar (el caso queda documentado). Todo queda registrado de forma inmutable para auditoría.',
    icon: 'ri-git-branch-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
  {
    id: 3,
    title: 'Reincidencia escala la sanción',
    description: 'El sistema registra el historial completo de cada usuario reportado. Una primera ofensa leve recibe advertencia; la segunda escala a silencio temporal; la tercera a bloqueo temporal; la cuarta a bloqueo permanente. La reincidencia agrava la consecuencia de forma automática y proporcional.',
    icon: 'ri-scales-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
  },
];

export function getReportsByType(type: ReportType | 'todas'): ReportEntry[] {
  if (type === 'todas') return reports;
  return reports.filter((r) => r.reportType === type);
}

export function getReportsByFlowState(state: ReportFlowState | 'todas'): ReportEntry[] {
  if (state === 'todas') return reports;
  return reports.filter((r) => r.flowState === state);
}

export function getReportsPending(): ReportEntry[] {
  return reports.filter((r) => r.flowState === 'reportado' || r.flowState === 'en-revision');
}

export function getReportsResolved(): ReportEntry[] {
  return reports.filter((r) => r.flowState === 'resuelto' || r.flowState === 'cerrado');
}

export function getReincidenciasByStatus(status: ReincidenciaEntry['status'] | 'todas'): ReincidenciaEntry[] {
  if (status === 'todas') return reincidencias;
  return reincidencias.filter((r) => r.status === status);
}